import { useCallback, useEffect, useMemo, useState } from "react";
import {
	boardDepartedTrain,
	declarePhase,
	initialProgressFor,
	observablePhase,
	observePhase,
	scheduledProgress,
	phasesForJourney,
	resetToObserved,
	shouldReset,
	startedOnAnotherDay,
	startPhaseFor,
	type JourneyProgress,
	type PhaseSignals,
} from "../core/rider-journey";
import type { RiderPhase } from "../shared/types";
import { isStickySession } from "../core/sticky";
import { lastSeenMs } from "./lastSeen";

/** The two journeys are kept apart: this morning's "arrived" is not the afternoon's answer. */
function storageKey(journeyId: string): string {
	return `gotime.journey.${journeyId}`;
}

/** A journey belongs to the day it started; yesterday's has nothing to say about this one. */
function today(): string {
	return new Date().toLocaleDateString("en-CA");
}

function read(journeyId: string, initial: JourneyProgress): JourneyProgress {
	// An hour untouched ends the journey as surely as a new day does. Without this a morning
	// stepped through by hand was still on screen at bedtime, waiting to be swiped back.
	if (!isStickySession(lastSeenMs(), Date.now())) {
		return initial;
	}
	try {
		const raw = localStorage.getItem(storageKey(journeyId));
		if (!raw) {
			return initial;
		}
		const stored = JSON.parse(raw) as { date: string; progress: JourneyProgress };
		return stored.date === today() ? stored.progress : initial;
	} catch {
		return initial;
	}
}

/**
 * Where the rider is on this journey, kept across reloads.
 *
 * Reopening the app halfway to the station used to start the morning again from nothing, which
 * is exactly when a phone gets picked up.
 */
export interface JourneyControls {
	declare: (phase: RiderPhase) => void;
	/** Drop the presses and go back to what the phone can see. */
	reset: () => void;
	/** A train has left the platform. Comes from the timetable, so it arrives with the snapshot. */
	board: (lastDepartureMs: number) => void;
}

export function useJourneyProgress(
	journeyId: string,
	hasAccessLeg: boolean,
	signals: PhaseSignals,
	nowMs: number,
	automatic = true,
): [JourneyProgress, JourneyControls] {
	const initial = useMemo(() => initialProgressFor(hasAccessLeg), [hasAccessLeg]);
	const phases = useMemo(() => phasesForJourney(hasAccessLeg), [hasAccessLeg]);
	const startPhase = startPhaseFor(hasAccessLeg);

	const [progress, setProgress] = useState<JourneyProgress>(() => read(journeyId, initial));
	// Swapped during render rather than in an effect: an effect would let the write below save
	// this journey's progress under the other journey's key first.
	const [loaded, setLoaded] = useState(journeyId);
	if (loaded !== journeyId) {
		setLoaded(journeyId);
		setProgress(read(journeyId, initial));
	}

	useEffect(() => {
		try {
			localStorage.setItem(storageKey(journeyId), JSON.stringify({ date: today(), progress }));
		} catch {
			// Private browsing; the journey simply will not survive a reload.
		}
	}, [journeyId, progress]);

	// A phase, not the signals, so the reducer only hears from the evidence when it changes.
	const observed = observablePhase(signals, phases, automatic);
	useEffect(() => {
		setProgress((current) => observePhase(scheduledProgress(current, hasAccessLeg, automatic), observed, Date.now()));
	}, [observed, hasAccessLeg, automatic]);

	useEffect(() => {
		setProgress((current) => (shouldReset(current, nowMs) ? initial : current));
	}, [nowMs, initial]);

	// `read` dates the stored copy, but only a mount consults it, and the native shell can keep this
	// page alive for days. Without this a journey nobody finished simply never ends.
	//
	// Rolled during render rather than in an effect, for the same reason as the swap above: effects
	// run after the commit, so the report for that commit carries the old day. On 2026-09-16 the
	// phone woke in the car at 07:24 still holding yesterday's `arrived`, posted it, and the
	// follower was told "Eben is at school" while the two of them were sitting in it.
	const [day, setDay] = useState(today);
	const currentDay = today();
	if (startedOnAnotherDay(day, currentDay)) {
		setDay(currentDay);
		setProgress(initial);
	}

	const declare = useCallback((phase: RiderPhase) => {
		setProgress((current) => declarePhase(current, phase, Date.now()));
	}, []);

	const reset = useCallback(() => {
		setProgress((current) => resetToObserved(current, Date.now(), startPhase));
	}, [startPhase]);

	const board = useCallback((lastDepartureMs: number) => {
		setProgress((current) => boardDepartedTrain(current, lastDepartureMs, Date.now()));
	}, []);

	return [progress, { declare, reset, board }];
}
